import { toPageContentEntry } from "../pageContentEntry"
import type { PageContentEntry } from "../pageContentEntry"

/**
 * Pure helpers extracted from UpdatePageContainer (SCRUM-910) so the container
 * shrinks to wiring and these become unit-testable in isolation. No React /
 * redux here — inputs in, plain data out.
 */

/** A template's declaration of one content variable. */
type TemplateDeclaration = {
  variable?: string
  type?: string
  complextype?: string
  char_number?: unknown
  value?: unknown
}

/** The part of a page template these helpers read. */
type PageTemplateLike = {
  content_variables?: {
    ContentRequired?: TemplateDeclaration[]
    ContentOptional?: TemplateDeclaration[]
  } | null
} | null

/** A stored `page_content` entry, possibly still carrying its declaration copy. */
type StoredContentEntry = {
  variable?: string
  value?: unknown
  type?: string
  complextype?: string
  char_number?: unknown
  variabletype?: string
  _id?: unknown
}

/**
 * Index a template's content variables by name, with the column each sits in.
 */
function declarationsByVariable(
  template?: unknown
): Map<string, { decl: TemplateDeclaration; variabletype: string }> {
  const byName = new Map<string, { decl: TemplateDeclaration; variabletype: string }>()
  if (!(template as PageTemplateLike)?.content_variables) return byName
  for (const variabletype of ["ContentRequired", "ContentOptional"] as const) {
    for (const decl of (template as NonNullable<PageTemplateLike>).content_variables![variabletype] || []) {
      if (decl && typeof decl.variable === "string")
        byName.set(decl.variable, { decl, variabletype })
    }
  }
  return byName
}

/**
 * Resolve each stored entry's DECLARATION — the fields that decide which input
 * component renders — from the template, keyed by variable name.
 *
 * Deliberately narrower than `mergePageContentWithTemplate` below, which is the
 * main page editor's loader: this adds nothing, drops nothing, reorders nothing
 * and never touches `value`. It is the read-side answer to SCRUM-1508: the
 * pending-pages and navigation/footer editors choose their input with
 * `BASIC_CONTENT_TYPES.includes(contentVar.type)` off the PAGE's stored `type`,
 * and 52 of 289 compared entries on the 43 live pending pages disagree with
 * their template there (measured 2026-09-08) — so a `faq` variable still stored
 * as `String` counts as basic and renders as a plain textarea.
 *
 * The result is for RENDERING only. Callers must keep passing their own
 * unresolved state to the save, because the stored copy is SCRUM-1472's slice 4
 * and migrating it from an edit screen would be an uncontrolled partial
 * migration. `declarationResolvedFromTemplate.test.jsx` pins that.
 *
 * An entry the template does not declare is returned untouched — off-template
 * entries stay editable on these screens, which is existing pinned behaviour.
 */
export function resolveContentDeclarations<E extends StoredContentEntry>(
  pageContent?: E[] | null,
  template?: unknown
  // `any[]`, as in the JSDoc this replaces: UpdateHistoriesPages.jsx and
  // UpdatePageHeaderFooterView.tsx pass each entry's optional `type` straight
  // into BASIC_CONTENT_TYPES.includes(), which a typed return reds. It narrows
  // to E[] when those call sites guard the missing type.
): any[] {
  const content = pageContent || []
  const byName = declarationsByVariable(template)
  if (byName.size === 0) return content

  const resolved = content.map((entry) => {
    const declared = entry && byName.get(entry.variable as string)
    if (!declared) return entry
    const { decl, variabletype } = declared
    if (
      entry.type === decl.type &&
      entry.complextype === decl.complextype &&
      entry.char_number === decl.char_number &&
      entry.variabletype === variabletype
    ) {
      // Already in agreement — hand back the same object so a caller can tell
      // "nothing was resolved" by identity.
      return entry
    }
    return {
      ...entry,
      type: decl.type,
      complextype: decl.complextype,
      char_number: decl.char_number,
      variabletype,
    }
  })

  // Nothing needed resolving → hand back the array that came in, so callers can
  // tell a no-op by identity and pass the untouched document straight through.
  return resolved.every((entry, i) => entry === content[i]) ? content : resolved
}

/**
 * Merge a page's existing `page_content` values onto its template's variable
 * list. The template decides WHICH variables the page has and in which order;
 * existing entries contribute their saved `value` (matched by variable name).
 *
 * This is the page editor's SAVE state, so what it returns is what gets stored.
 * Since SCRUM-1514 that is the page's own data only — `variable` and `value` —
 * and never the declaration, which the template owns and
 * `resolveContentDeclarations` derives for rendering.
 *
 * Rules:
 *  - No template / no content_variables → return the existing content with a
 *    `variabletype` defaulted to "ContentRequired" where missing. Unchanged:
 *    with no template there is nothing to derive from, so the entry's own tag is
 *    the only answer that exists.
 *  - Otherwise: one entry per template variable, in template order, carrying the
 *    existing value where there is one and the template's default otherwise.
 *  - Legacy support: existing variables NOT present in the template are appended
 *    unchanged (their `variabletype` defaulted to "ContentOptional" where
 *    missing). Also unchanged, and for the same reason the SCRUM-1511 migration
 *    is conditional: for an off-template entry the stored copy is the only
 *    declaration there is.
 */
export function mergePageContentWithTemplate<E extends StoredContentEntry>(
  existingContent?: E[] | null,
  templateArg?: unknown
): Array<PageContentEntry | (E & { variabletype: string })> {
  const template = templateArg as PageTemplateLike
  // If no template or no content_variables, try to preserve existing content with variabletype
  if (!template?.content_variables) {
    // Try to set variabletype based on existing data if missing
    return (existingContent || []).map((item) => ({
      ...item,
      variabletype: item.variabletype || "ContentRequired", // Default to ContentRequired
    }));
  }

  const templateVars = [
    ...(template.content_variables?.ContentRequired || []),
    ...(template.content_variables?.ContentOptional || []),
  ];

  // Map existing content by variable name for quick lookup
  const existingMap: Record<string, E> = {};
  (existingContent || []).forEach((item) => {
    if (item.variable) {
      existingMap[item.variable] = item;
    }
  });

  // Merge: the template decides WHICH variables the page has and in which order;
  // the existing entry contributes its authored `value`.
  //
  // SCRUM-1514: an entry the template declares carries the variable and its
  // value and nothing else. This used to spread the whole template variable
  // (`{...c, variabletype}`), so `type`, `complextype`, `char_number`,
  // `variabletype` and the template variable's `_id` were merged in on load and
  // then saved back on every edit — the highest-volume way the copy the
  // SCRUM-1511 migration removes came back. The declaration is derived for
  // rendering instead, by `resolveContentDeclarations` above, which the view
  // applies; see UpdatePageView.
  const mergedVars = templateVars.map((templateVar) => {
    const existing = existingMap[templateVar.variable as string];
    const value =
      existing && existing.value !== undefined
        ? existing.value
        : templateVar.value;
    // `_id`: the stored entry's own one where it has one, and nothing otherwise
    // (SCRUM-1548). This is the load path of the main editor, so stripping a
    // stored `_id` here would be a silent migration on every edit — that is
    // SCRUM-1520's audited apply — while an entry that has none must not gain
    // the template variable's on its next save.
    return toPageContentEntry(templateVar, value, existing?._id)
  });

  // Also include any existing variables that might not be in the template (legacy support)
  const mergedVarNames = new Set(mergedVars.map((v) => v.variable));
  const extraVars = (existingContent || [])
    .filter((item) => item.variable && !mergedVarNames.has(item.variable))
    .map((item) => ({
      ...item,
      variabletype: item.variabletype || "ContentOptional",
    }));

  return [...mergedVars, ...extraVars];
}

/**
 * The page-type step's configuration, by page type: the slug-based types carry
 * the chosen name, the entity types the chosen doc.
 */
type PageTypeConf =
  | { type: "simple_page" | "error" | "maintenance" | "redirect"; object: string }
  | { type: "category"; object: { slug: string } }
  | { type: "sub_category"; object: { slug: string; category: { slug: string } } }
  | { type: "product"; object: { path: string } }
  | { type: "post"; object: { pathpost: string } }
  | { type: "author"; object: { pathauthor: string } }
  | { type: "default"; object?: unknown }

type DerivedPageConfiguration = { type: string; object_target: unknown; path: string }

/**
 * Derive a page's `page_configuration` object from the page-type step's
 * pick (UpdatePageContainer's `pageTypeSelection`, SCRUM-2216). Pure mapping core of the container's
 * `preparePageConfiguration` (the `setPage` wrapper stays in the component).
 *
 * Per-type path derivation is verbatim from the legacy code (spaces → dashes for
 * the slug-based types; `default` blanks the type/path/object_target).
 */
export function derivePageConfiguration(pageTypeConf: PageTypeConf): DerivedPageConfiguration {
  const configuration: DerivedPageConfiguration = {
    type: pageTypeConf.type,
    object_target: pageTypeConf.object,
    path: "",
  };
  if (
    pageTypeConf.type === "simple_page" ||
    pageTypeConf.type === "error" ||
    pageTypeConf.type === "maintenance" ||
    pageTypeConf.type === "redirect"
  ) {
    configuration.path = pageTypeConf.object.split(" ").join("-");
    configuration.object_target = {};
  }
  if (pageTypeConf.type === "category") {
    configuration.path = pageTypeConf.object["slug"].split(" ").join("-");
  }
  if (pageTypeConf.type === "sub_category") {
    configuration.path =
      pageTypeConf.object.category["slug"].split(" ").join("-") +
      "/" +
      pageTypeConf.object["slug"].split(" ").join("-");
  }
  if (pageTypeConf.type === "product") {
    configuration.path = pageTypeConf.object["path"];
  }
  if (pageTypeConf.type === "post") {
    configuration.path = pageTypeConf.object["pathpost"];
  }
  if (pageTypeConf.type === "author") {
    configuration.path = pageTypeConf.object["pathauthor"];
  }
  if (pageTypeConf.type === "default") {
    configuration.type = "";
    configuration.path = "";
    configuration.object_target = {};
  }
  return configuration;
}

/**
 * SCRUM-1587: the "Redirect from (old paths)" textarea, one path per line, to
 * the array `redirect_from` stores. Each value is normalised to the shape the
 * site's resolver matches on — `page_configuration.path`'s own: no leading
 * slash, one trailing slash — so `/oud/pad` becomes `oud/pad/`. Whitespace and
 * blank lines are dropped, repeats collapsed. Mirror of the server's
 * `utils/redirectFrom.js`, which applies the same rule to whatever arrives.
 */
export function normalizeRedirectFromText(text: string | null | undefined): string[] {
  const out: string[] = [];
  for (const line of String(text ?? "").split(/\r?\n/)) {
    const base = line.trim().replace(/^\/+/, "").replace(/\/+$/, "");
    if (!base) continue;
    const value = `${base}/`;
    if (!out.includes(value)) out.push(value);
  }
  return out;
}

/**
 * True when one of the normalised entries is the page's own path — a redirect
 * onto itself, refused as a field error before the save leaves the screen.
 * Compared without leading/trailing slashes, since stored paths carry either
 * (`privacy-oud/` in the seed, `credit-cards/travel` when derived).
 */
export function redirectFromHasOwnPath(values: string[], ownPath: string | undefined): boolean {
  const own = String(ownPath ?? "").trim().replace(/^\/+/, "").replace(/\/+$/, "");
  if (!own) return false;
  return values.some((v) => v.replace(/\/+$/, "") === own);
}

/**
 * SCRUM-1637: the sentence the refused-save notification carries. Mirrors the
 * conditions `validInputsChecker` (UpdatePageContainer.jsx) scores, so the
 * toast names the same fields the red lines mark — kept pure so a test can pin
 * the wording without rendering the form. `page` is the form state,
 * `redirectFromText` the raw redirect-from textarea value, `oldPage` the page as
 * loaded (its stored path is the loop check). Returns one clause per refused
 * input, joined with "; ".
 */
export function describeRefusedInputs(
  page: { global_title?: string; meta_description?: string; page_configuration?: { type?: string } } | null | undefined,
  redirectFromText: string | null | undefined,
  oldPage: { page_configuration?: { path?: string } } | null | undefined
): string {
  const reasons: string[] = [];
  if (!page?.global_title?.length) reasons.push("the global title is empty");
  if (!page?.meta_description?.length) reasons.push("the META description is empty");
  if (!page?.page_configuration?.type) {
    reasons.push(
      "the page type configuration is incomplete — select the page's category, subcategory or product again under \"Selected Page Type\""
    );
  }
  if (
    redirectFromHasOwnPath(
      normalizeRedirectFromText(redirectFromText ?? ""),
      oldPage?.page_configuration?.path
    )
  ) {
    reasons.push("a redirect-from path points at the page's own path");
  }
  return reasons.length ? reasons.join("; ") : "an input did not validate";
}
